import React, { useEffect, useState, useContext } from "react";
import {
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
  Form,
  Badge,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { useNavigate } from "react-router-dom";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import HusariaHeader from "../components/HusariaHeader";

const EmailQueue = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();

  const [emails, setEmails] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const rowKey = "email_queue_uuid";
  // Test email modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmailDomain, setTestEmailDomain] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const visibleColumns = [
    { key: "email_date_formatted", label: "Date" },
    { key: "email_time_formatted", label: "Time" },
    { key: "hostname", label: "Hostname" },
    { key: "email_from", label: "From" },
    { key: "email_to", label: "To" },
    { key: "email_subject", label: "Subject" },
    { key: "email_status", label: "Status" },
    { key: "status", label: "Retry" },
    { key: "email_action_after ", label: "After Email" },
  ];
  const fetchEmailQueue = async () => {
    setLoading(true);
    setError(null);
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const response = await fetch(
        `${serverHost}/email_queue/${site.site_uuid}`
      );
      if (!response.ok) throw new Error("Failed to fetch email queue");
      const data = await response.json();
      let email_data = data.result;
      email_data.sort((a, b) => {
        return new Date(b.email_date_formatted) - new Date(a.email_date_formatted);
      });
      setEmails(email_data);
      console.log("Email queue fetched");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailDomain.trim()) {
      setTestResult({
        success: false,
        message: "Please enter an email domain",
      });
      return;
    }

    setSendingTest(true);
    setTestResult(null);
    try {
      const response = await fetch(`${serverHost}/send_test_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: testEmailDomain }),
      });

      if (!response.ok) throw new Error("Failed to send test email");

      const data = await response.json();
      setTestResult({
        success: true,
        message: data.message || "Test email sent successfully",
      });

      // Refresh queue after sending test
      setTimeout(() => fetchEmailQueue(), 1000);
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setSendingTest(false);
    }
  };

  const handleRetryEmail = async (emailId) => {
    try {
      const response = await fetch(`${serverHost}/retry_email/${emailId}`, {
        method: "POST",
      });

      if (!response.ok) throw new Error("Failed to retry email");

      console.log(`Email ${emailId} retry initiated`);
      fetchEmailQueue();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (index === 0) {
        return (
          <a
            onClick={() => navigate(`/admin/hucs/view-email/${row[rowKey]}`)}
            style={{
              color: isDark ? "#60a5fa" : "#0d6efd",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {cell ?? "-"}
          </a>
        );
      }
      if (index === 6) {
        const statusLower = cell?.toLowerCase();
        if (statusLower === "sent") return <Badge bg="success">Sent</Badge>;
        if (statusLower === "waiting") return <Badge bg="info">Waiting</Badge>;
        if (statusLower === "trying")
          return (
            <Badge bg="warning" text="dark">
              Trying
            </Badge>
          );
        if (statusLower === "failed") return <Badge bg="danger">Failed</Badge>;
        return <Badge bg="secondary">{cell || "Unknown"}</Badge>;
      }
      return cell ?? "-";
    },
  }));

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      console.log(row[rowKey]);
      navigate(`/admin/hucs/view-email/${row[rowKey]}`);
    },
  };

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredRows = React.useMemo(() => {
    if (!search && statusFilter === "all") return emails;

    return emails.filter(
      (row) =>
        (statusFilter === "all" || row.email_status === statusFilter) &&
        visibleColumns.some((col) =>
          String(row[col.key] ?? "")
            .toLowerCase()
            .includes(search.toLowerCase())
        )
    );
  }, [emails, search, visibleColumns]);

  useEffect(() => {
    fetchEmailQueue();
  }, []);

  const getStatusCount = (status) => {
    return emails.filter(
      (email) => email.email_status?.toLowerCase() === status
    ).length;
  };

  return (
    <>
      <HusariaHeader name="Email Queue" parentName="HUCS" padding="4.5rem" />

      <Container fluid className="mt--6">
        {/* Email Queue Table */}
        <Card
          className={`shadow-sm ${
            isDark ? "bg-dark text-light border-dark" : ""
          }`}
        >
          <Card.Header
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            <div className="d-flex align-items-center">
              <h4 className="mb-2 mb-md-0">
                <Badge
                  bg="primary"
                  className="ms-2"
                  style={{ marginLeft: "0.5rem", marginRight: "1rem" }}
                >
                  {filteredRows.length} / {emails.length} Emails
                </Badge>
              </h4>
              <Form className="navbar-search navbar-search-light form-inline search-border">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>
                    <Input
                      className="table-search-bar form-control "
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
              {/*<h6 style={{ paddingLeft: "1rem" }}>
                note: the row here is temp data so the email details can be
                displayed
              </h6>*/}
            </div>

            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div
                style={{
                  paddingRight: "1rem",
                  overflow: "hidden",
                  borderRadius: "2rem",
                }}
              >
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="sm"
                  style={{
                    width: "150px",
                    backgroundColor: isDark ? "#374151" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                    borderColor: isDark ? "#4b5563" : "#ced4da",
                    borderRadius: "2rem",
                    paddingLeft: "1rem",
                  }}
                >
                  <option value="all">All Status ({emails.length})</option>
                  <option value="waiting">
                    Waiting ({getStatusCount("waiting")})
                  </option>
                  <option value="trying">
                    Trying ({getStatusCount("trying")})
                  </option>
                  <option value="sent">Sent ({getStatusCount("sent")})</option>
                  <option value="failed">
                    Failed ({getStatusCount("failed")})
                  </option>
                </Input>
              </div>
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowTestModal(true)}
              >
                <i
                  className="fas fa-paper-plane me-1"
                  style={{ paddingRight: "5px" }}
                ></i>
                Test Email
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={fetchEmailQueue}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div>
              <div className="py-4 table-responsive">
                <BootstrapTable
                  keyField={rowKey}
                  data={filteredRows} // <-- use filteredRows instead of all emails
                  columns={columns}
                  bootstrap4
                  pagination={pagination}
                  bordered={false}
                  hover
                  rowEvents={rowEvents}
                  rowStyle={{ cursor: "pointer" }}
                  classes="align-items-center table-flush"
                  noDataIndication={() =>
                    loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="text-center">No records found.</div>
                    )
                  }
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Test Email Modal */}
      <Modal
        show={showTestModal}
        onHide={() => {
          setShowTestModal(false);
          setTestEmailDomain("");
          setTestResult(null);
        }}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Send Test Email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Email Domain</Form.Label>
            <Form.Control
              type="text"
              placeholder="example.com or user@example.com"
              value={testEmailDomain}
              onChange={(e) => setTestEmailDomain(e.target.value)}
              style={{
                backgroundColor: isDark ? "#374151" : "#fff",
                color: isDark ? "#e2e8f0" : "#212529",
                borderColor: isDark ? "#4b5563" : "#ced4da",
              }}
            />
            <Form.Text style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
              Enter the domain or email address to send a test email to
            </Form.Text>
          </Form.Group>

          {testResult && (
            <Alert variant={testResult.success ? "success" : "danger"}>
              {testResult.message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button
            variant="light"
            onClick={() => {
              setShowTestModal(false);
              setTestEmailDomain("");
              setTestResult(null);
            }}
          >
            Close
          </Button>
          <Button
            variant="success"
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmailDomain.trim()}
          >
            {sendingTest ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <i
                  className="fas fa-paper-plane me-2"
                  style={{ paddingRight: "0.5rem" }}
                ></i>
                Send Test
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailQueue;
